/**
 * Keeps keyboard focus inside a container while `active` (a modal drawer): focus moves in on
 * activation (to `[data-autofocus]`, else the first control), Tab and Shift+Tab cycle at the edges,
 * Escape calls `onEscape`, and on deactivation focus goes back to `returnTo` (the control that
 * opened the container). Used where a panel needs one; `Modal` keeps its own focus handling and shares `cycleFocus`.
 */

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Wraps Tab at the first and last control, and pulls it back in when focus is outside. */
export function cycleFocus(event: KeyboardEvent, root: HTMLElement) {
  const all = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  const first = all.at(0);
  const last = all.at(-1);
  if (!first || !last) return;
  const current = document.activeElement;
  // The container itself (a dialog focused on opening) counts as outside its controls: Shift+Tab from it would
  // otherwise leave for the page before it.
  const outside = !root.contains(current) || current === root;
  const edge = event.shiftKey ? first : last;
  if (current !== edge && !outside) return;
  const wrapTo = event.shiftKey ? last : first;
  event.preventDefault();
  wrapTo.focus();
}

export function useFocusTrap(
  container: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape: () => void,
  returnTo: RefObject<HTMLElement | null>,
) {
  // The latest callback without re-running the trap (which would steal focus) on every render.
  const escape = useRef(onEscape);
  useEffect(() => {
    escape.current = onEscape;
  });

  useEffect(() => {
    const root = container.current;
    if (!active || !root) return;
    const opener = returnTo.current;
    (root.querySelector<HTMLElement>('[data-autofocus]') ?? root.querySelector<HTMLElement>(FOCUSABLE) ?? root).focus();

    function onKeyDown(event: KeyboardEvent) {
      // A control inside (a select, an open menu) that already used the key keeps it.
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') escape.current();
      else if (event.key === 'Tab' && root) cycleFocus(event, root);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // A container that stays on screen (a drawer that became a docked column) keeps its focus.
      if (root.hidden || !root.isConnected || !root.contains(document.activeElement)) opener?.focus();
    };
  }, [active, container, returnTo]);
}
